/**
 * tcAssistantIntents — canned intent detection + response templates for the
 * Talent Connect robot avatar chat.
 *
 * NO LLM. Simple keyword matching (case-insensitive, EN + ES) with context
 * injection. Runs entirely in the browser. Context is provided by the caller
 * from live component state (analytics data, room id, plan, etc.).
 *
 * To add a new intent: append to INTENTS below with keywords_en / keywords_es
 * and a respond(ctx, lang) function returning a string.
 */

/**
 * @typedef {Object} ChatContext
 * @property {number|null} totalInterviews
 * @property {number|null} qualificationPercentage
 * @property {number|null} dollarsSaved
 * @property {number|null} hoursSaved
 * @property {'free'|'business'} plan
 * @property {string|null} activeRoomId
 * @property {string|null} sessionId  // shortened
 * @property {boolean} analyticsLoading
 */

// -------- helpers --------

function num(v, fallback = 0) {
  return Number.isFinite(v) ? v : fallback;
}

function pct(v) {
  return `${num(v).toFixed(1)}%`;
}

function dollars(v) {
  return `$${num(v).toLocaleString('en-US')}`;
}

// -------- intents --------

const INTENTS = [
  // 1. metrics
  {
    id: 'metrics',
    keywords_en: ['metric', 'stat', 'summary', 'dashboard', 'kpi', 'analytics', 'today\'s metric', "today's metric"],
    keywords_es: ['métric', 'metric', 'estadístic', 'estadistic', 'resumen', 'analytics', 'analitic', 'kpi'],
    respond: (ctx, lang) => {
      if (ctx.analyticsLoading) {
        return lang === 'es'
          ? '⏳ Cargando métricas… probá de nuevo en un segundo.'
          : '⏳ Loading metrics… try again in a second.';
      }
      const total = num(ctx.totalInterviews);
      if (lang === 'es') {
        const base = `📊 **Métricas de hoy:**\n• Entrevistas totales: ${total}`;
        if (ctx.plan === 'business') {
          return `${base}\n• Tasa de calificación: ${pct(ctx.qualificationPercentage)}\n• Dólares ahorrados: ${dollars(ctx.dollarsSaved)}\n• Horas ahorradas: ${num(ctx.hoursSaved).toFixed(1)}h`;
        }
        return `${base}\n\n💎 Actualizá a **Business** para ver tasa de calificación y métricas de ROI completas.`;
      }
      const base = `📊 **Today's metrics:**\n• Total interviews: ${total}`;
      if (ctx.plan === 'business') {
        return `${base}\n• Qualification rate: ${pct(ctx.qualificationPercentage)}\n• Dollars saved: ${dollars(ctx.dollarsSaved)}\n• Hours saved: ${num(ctx.hoursSaved).toFixed(1)}h`;
      }
      return `${base}\n\n💎 Upgrade to **Business** to see qualification rate and full ROI metrics.`;
    },
  },
  // 2. room_code
  {
    id: 'room_code',
    keywords_en: ['room code', 'room id', 'current room', 'room number', 'active room', 'room'],
    keywords_es: ['código', 'codigo', 'sala', 'room', 'id de sala', 'sala activa'],
    respond: (ctx, lang) => {
      if (ctx.activeRoomId) {
        return lang === 'es'
          ? `🏷️ Tu sala activa es: **${ctx.activeRoomId}**\n\nCompartí este código con el candidato o hacé que escanee el QR del dashboard.`
          : `🏷️ Your active room is: **${ctx.activeRoomId}**\n\nShare this code with the candidate or have them scan the QR from the dashboard.`;
      }
      return lang === 'es'
        ? '🏷️ No hay sala activa. Volvé al dashboard y presioná **New Room** para crear una.'
        : '🏷️ No active room. Go to the dashboard and hit **New Room** to create one.';
    },
  },
  // 3. qualification
  {
    id: 'qualification',
    keywords_en: ['qualification', 'qualify', 'pass rate', 'pass-rate', 'success rate', 'passing'],
    keywords_es: ['calificación', 'calificacion', 'aprobación', 'aprobacion', 'tasa', 'aprobados'],
    respond: (ctx, lang) => {
      if (ctx.plan !== 'business') {
        return lang === 'es'
          ? '📈 La tasa de calificación es una métrica de **Business**. Actualizá tu plan desde /talent-connect/pricing para desbloquearla.'
          : '📈 Qualification rate is a **Business** metric. Upgrade your plan at /talent-connect/pricing to unlock it.';
      }
      const p = pct(ctx.qualificationPercentage);
      const total = num(ctx.totalInterviews);
      return lang === 'es'
        ? `📈 Tu tasa de calificación actual es **${p}** sobre ${total} entrevistas completadas.`
        : `📈 Your current qualification rate is **${p}** across ${total} completed interviews.`;
    },
  },
  // 4. upgrade
  {
    id: 'upgrade',
    keywords_en: ['upgrade', 'business', 'plan', 'pricing', 'price', '350', 'billing'],
    keywords_es: ['actualizar', 'upgrade', 'business', 'plan', 'precio', 'pricing', '350', 'billing'],
    respond: (ctx, lang) => {
      if (ctx.plan === 'business') {
        return lang === 'es'
          ? '✨ Ya estás en el plan **Business** — analytics, ROI y unlimited rooms desbloqueados. ¿Necesitás algo más?'
          : "✨ You're already on the **Business** plan — analytics, ROI and unlimited rooms unlocked. Anything else?";
      }
      return lang === 'es'
        ? '⬆️ El plan **Business** ($599.00/mes, con **30 días de prueba gratis** sin tarjeta) desbloquea:\n• Salas ilimitadas\n• Dashboard de analytics completo\n• ROI (dólares + horas ahorradas)\n• Preguntas custom\n• Branding propio\n\n👉 Andá a /talent-connect/pricing para ver todos los detalles.'
        : '⬆️ The **Business** plan ($599.00/mo, with a **30-day free trial** — no credit card required) unlocks:\n• Unlimited interview rooms\n• Full analytics dashboard\n• ROI (dollars + hours saved)\n• Custom questions\n• Custom branding\n\n👉 Go to /talent-connect/pricing for full details.';
    },
  },
  // 5. help
  {
    id: 'help',
    keywords_en: ['help', 'what can you do', 'commands', 'options', 'assist', '?'],
    keywords_es: ['ayuda', 'qué podés', 'que podes', 'qué puedes', 'comandos', 'opciones', 'ayudame', 'ayúdame'],
    respond: (_ctx, lang) => (
      lang === 'es'
        ? '❓ Podés preguntarme sobre:\n• 📊 Métricas de hoy\n• 🏷️ Código de sala actual\n• 📈 Tasa de calificación\n• ⬆️ Info del plan Business\n\nO usá los chips rápidos abajo ⬇️'
        : '❓ You can ask me about:\n• 📊 Today\'s metrics\n• 🏷️ Current room code\n• 📈 Qualification rate\n• ⬆️ Business plan info\n\nOr use the quick chips below ⬇️'
    ),
  },
];

// -------- detection --------

/**
 * Match user text against intents. Returns the first matching intent id or 'fallback'.
 * Case-insensitive substring match across BOTH language keyword sets so a
 * Spanish user typing an English word still lands on the right intent.
 */
export function detectIntent(text) {
  if (!text) return 'fallback';
  const t = text.trim().toLowerCase();
  if (!t) return 'fallback';
  for (const intent of INTENTS) {
    for (const kw of intent.keywords_en) {
      if (t.includes(kw)) return intent.id;
    }
    for (const kw of intent.keywords_es) {
      if (t.includes(kw)) return intent.id;
    }
  }
  return 'fallback';
}

export function respondForIntent(intentId, ctx, lang) {
  const intent = INTENTS.find((i) => i.id === intentId);
  if (!intent) {
    return lang === 'es'
      ? '🤔 Perdón, no puedo responder eso todavía. Probá una de las acciones rápidas abajo ⬇️'
      : "🤔 Sorry, I can't answer that yet. Try one of the quick actions below ⬇️";
  }
  return intent.respond(ctx, lang);
}

/**
 * Phase 13 fallback — combines detectIntent + respondForIntent into a single
 * call for use when the streaming LLM endpoint fails (503 / timeout / abort).
 * Returns the canned response string for the closest matching intent, or
 * the generic fallback message if no intent matched.
 */
export function runCannedFallback(userMessage, ctx, lang) {
  const intentId = detectIntent(userMessage);
  return respondForIntent(intentId, ctx, lang);
}

/** Quick chips displayed above the input. Order matters.
 *  Phase 13 note: `text` is now a natural-language prompt sent to the LLM
 *  (previously used verbatim as a keyword to trigger canned intents; the
 *  new prompts are richer so the model produces a substantive answer). */
export function getQuickChips(lang) {
  if (lang === 'es') {
    return [
      { id: 'metrics', label: '📊 Ver métricas de hoy', text: 'Mostrame las métricas de entrevistas de hoy' },
      { id: 'room_code', label: '🏷️ Código de sala', text: '¿Cuál es el código de mi sala activa?' },
      { id: 'qualification', label: '📈 Tasa de calificación', text: '¿Cuál es mi tasa de calificación y cómo se calcula?' },
      { id: 'upgrade', label: '⬆️ Info de upgrade', text: '¿Cómo actualizo a Business y qué desbloquearía?' },
      { id: 'help', label: '❓ Ayuda', text: 'Ayuda — ¿qué podés hacer?' },
    ];
  }
  return [
    { id: 'metrics', label: "📊 Show today's metrics", text: "Show me today's interview metrics" },
    { id: 'room_code', label: '🏷️ Current room code', text: "What's my current active room code?" },
    { id: 'qualification', label: '📈 Qualification rate', text: "What's my qualification rate and how is it calculated?" },
    { id: 'upgrade', label: '⬆️ Upgrade info', text: 'How do I upgrade to Business and what would I unlock?' },
    { id: 'help', label: '❓ Help', text: 'Help — what can you do?' },
  ];
}

export function getWelcomeMessage(lang) {
  return lang === 'es'
    ? '👋 ¡Hola! Puedo ayudarte con métricas de entrevistas, códigos de sala y datos de tu cuenta. Probá una de las acciones rápidas o escribí una pregunta.'
    : '👋 Hi! I can help you with interview metrics, room codes, and account info. Try one of the quick actions below or type a question.';
}

/** Tooltip suggestions — rotated by the proactive timer. */
export const TOOLTIP_SUGGESTIONS = {
  en: [
    "Need a quick summary of today's interview metrics?",
    'Want to see your qualification rate?',
    'Upgrade to Business for full analytics.',
    'Ask me for your current room code.',
  ],
  es: [
    '¿Querés un resumen rápido de las métricas de hoy?',
    '¿Ver tu tasa de calificación?',
    'Actualizá a Business para ver analytics completos.',
    'Preguntame por el código de tu sala actual.',
  ],
};

/** Detect default UI language from browser. */
export function detectDefaultLang() {
  if (typeof navigator === 'undefined') return 'en';
  return String(navigator.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
}

// -------- localStorage helpers (single source of truth for keys) --------

export const LANG_KEY = 'tc:chat-lang';
export const TOOLTIPS_DISABLED_KEY = 'tc:tooltips-disabled';

export function readStoredLang() {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v === 'en' || v === 'es') return v;
  } catch { /* noop */ }
  return detectDefaultLang();
}

export function writeStoredLang(lang) {
  try { localStorage.setItem(LANG_KEY, lang); } catch { /* noop */ }
}

export function tooltipsDisabled() {
  try { return localStorage.getItem(TOOLTIPS_DISABLED_KEY) === 'true'; } catch { return false; }
}

export function disableTooltips() {
  try { localStorage.setItem(TOOLTIPS_DISABLED_KEY, 'true'); } catch { /* noop */ }
}

// -------- Phase 15 (3/4) — ASSISTANT_GUIDED_TOUR --------

export const GUIDED_TOUR_KEY = 'tc_guided_tour_seen';

export function hasSeenGuidedTour() {
  try { return localStorage.getItem(GUIDED_TOUR_KEY) === 'true'; }
  catch { return false; }
}

export function markGuidedTourSeen() {
  try { localStorage.setItem(GUIDED_TOUR_KEY, 'true'); }
  catch { /* noop */ }
}

/** Three-step onboarding messages appended to the chat on first open. */
export function getGuidedTourSteps(lang) {
  if (lang === 'es') {
    return [
      '👋 ¡Bienvenido/a! Soy tu **TC Assistant**. Voy a mostrarte lo básico en 3 pasos rápidos.',
      '📊 Pedime **métricas de hoy** o tu **tasa de calificación** cuando quieras — o usá los chips de abajo ⬇️',
      '💡 También podés **inyectar preguntas** al candidato en vivo. Escribí `ask ¿tenés experiencia con ASL?` o simplemente terminá con `?`. ¡Listo!',
    ];
  }
  return [
    "👋 Welcome! I'm your **TC Assistant**. Let me show you the basics in 3 quick steps.",
    "📊 Ask me for **today's metrics** or your **qualification rate** anytime — or use the chips below ⬇️",
    '💡 You can also **inject questions** to the live candidate. Type `ask do you have ASL experience?` or just end with `?`. Ready to go!',
  ];
}

// -------- Phase 15 (4/4) — REALTIME_QUESTION_INJECTION --------

const INJECTION_TRIGGERS = ['send question', 'push question', 'inject', 'ask'];

/**
 * Detect whether the recruiter's message should be forwarded to the
 * candidate's active room as an ad-hoc question. Per Phase 15 spec:
 *   • starts with `ask` / `inject` / `send question` / `push question` (ci), OR
 *   • ends with `?` and length >= 8
 * Empty/whitespace-only trigger payload returns { isEmpty: true } so the
 * caller can respond with "Please enter a valid question." and skip WS.
 *
 * When a trigger prefix is present it's stripped from the outgoing text so
 * the candidate sees a clean prompt.
 *
 * NOTE: callers should run detectIntent() first — if it matches a known
 * canned intent (metrics/room_code/qualification/upgrade/help), skip this
 * check so the existing chip flow keeps working.
 */
export function detectQuestionInjection(text) {
  const raw = String(text ?? '');
  const trimmed = raw.trim();
  if (!trimmed) return { isInjection: false, isEmpty: true, question: null };

  const lower = trimmed.toLowerCase();
  for (const trig of INJECTION_TRIGGERS) {
    if (lower.startsWith(trig)) {
      const nextChar = trimmed.charAt(trig.length);
      // Require a space/punct after the trigger so "asking" / "injected" don't false-match.
      if (nextChar === '' || /[\s:,\-]/.test(nextChar)) {
        const stripped = trimmed.slice(trig.length).replace(/^[\s:,\-]+/, '').trim();
        if (!stripped) return { isInjection: true, isEmpty: true, question: null };
        return { isInjection: true, isEmpty: false, question: stripped };
      }
    }
  }
  if (trimmed.endsWith('?') && trimmed.length >= 8) {
    return { isInjection: true, isEmpty: false, question: trimmed };
  }
  return { isInjection: false, isEmpty: false, question: null };
}
